import { Formik } from 'formik';
import React, { useMemo } from 'react';
import { Form } from 'react-bootstrap';

import { PrimaryButton } from '@components/Button/variants';
import Divider from '@components/Divider';
import AutocompleteField from '@components/Form/AutocompleteField';
import TextAreaField from '@components/Form/TextAreaField';
import TextField from '@components/Form/TextField';
import { useEntitiesCreate, useProfileUpdate } from '@hooks';
import { autocompleteEntities } from '@utils/backend-api/entities';
import { AutocompleteRecord } from '@utils/interfaces';

import ProfileSchema from './ProfileSchema';

interface ProfileProps {
  bio: string;
  company: AutocompleteRecord;
  isStaff: boolean;
  isDisabled?: boolean;
  location: AutocompleteRecord;
  oneLineBio: string;
  shortBio: string;
  title: AutocompleteRecord;
  userId: number;
  username: string;
}

const Profile = ({
  userId,
  oneLineBio,
  username,
  company,
  location,
  title,
  bio,
  shortBio,
  isStaff,
  isDisabled,
}: ProfileProps) => {
  const initialState = useMemo(
    () => ({
      username,
      oneLineBio: oneLineBio || '',
      company: company || '',
      location: location || '',
      title: title || '',
      bio: bio || '',
      shortBio: shortBio || '',
    }),
    [username, company, location, title, oneLineBio, shortBio, bio]
  );
  const { mutateAsync: createEntityAsync } = useEntitiesCreate();
  const { saveProfile, isSavingProfile, showSave } = useProfileUpdate({ userId });

  const handleSubmit = (values) => {
    const sanitizedParams = {
      username: values.username?.trim() || '',
      company_name: values.company.label || '',
      title_at_company: values.title.label || '',
      location: values.location.value || '',
      one_line_bio: values.oneLineBio || '',
      short_bio: values.shortBio || '',
      bio: values.bio || '',
    };

    saveProfile(sanitizedParams);
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialState}
      onSubmit={handleSubmit}
      validationSchema={ProfileSchema}
    >
      {(formik) => (
        <Form noValidate onSubmit={formik.handleSubmit}>
          <div className="section-container" id="Profile">
            <div className="section-header mt-2">Profile</div>
            <div className="section-detail mb-40">
              <div className="section-detail-container mb-24">
                <div className="section-description">
                  <div>Tell us about you</div>
                </div>
              </div>
              <div className="section-form-container">
                <TextField
                  disabled={isDisabled}
                  className="mb-24"
                  name="username"
                  label="Profile URL"
                  pretype="https://theinformation.com/u/"
                  placeholder="username"
                />
                <AutocompleteField
                  className="mb-24"
                  isDisabled={isDisabled}
                  label="Location"
                  name="location"
                  fetchMethod={(value) =>
                    autocompleteEntities({
                      fragment: value,
                      entity_type: 'location',
                      word_type: 'proper',
                    })
                  }
                  createMethod={(value) =>
                    createEntityAsync({
                      name: value,
                      entity_type: 'location',
                      word_type: 'proper',
                    })
                  }
                  placeholder="Type to search locations…"
                />
                <AutocompleteField
                  className="mb-24"
                  isDisabled={isDisabled}
                  label="Title"
                  name="title"
                  fetchMethod={(value) =>
                    autocompleteEntities({
                      fragment: value,
                      entity_type: 'job_title',
                    })
                  }
                  createMethod={(value) =>
                    createEntityAsync({
                      name: value,
                      entity_type: 'other',
                      word_type: 'common',
                    })
                  }
                  placeholder="Type to search titles…"
                />
                <AutocompleteField
                  className="mb-24"
                  isDisabled={isDisabled}
                  label="Company"
                  name="company"
                  fetchMethod={(value) =>
                    autocompleteEntities({
                      fragment: value,
                      entity_type: 'organization',
                      word_type: 'proper',
                    })
                  }
                  createMethod={(value) =>
                    createEntityAsync({
                      name: value,
                      entity_type: 'organization',
                      word_type: 'proper',
                    })
                  }
                  placeholder="Type to search organizations…"
                />
                <TextField
                  className="mb-24"
                  label="One-line bio / pronouns"
                  name="oneLineBio"
                  disabled={isDisabled}
                  maxCharCount={40}
                />
                {isStaff && (
                  <TextAreaField
                    className="mb-24"
                    disabled={isDisabled}
                    label="Short bio"
                    name="shortBio"
                    placeholder="Please enter a sentence or two description of yourself."
                  />
                )}
                <TextAreaField
                  disabled={isDisabled}
                  label="Bio"
                  name="bio"
                  placeholder="Short description about me"
                />
              </div>
            </div>
            <div className="save-action-container" id="profile-save">
              <PrimaryButton
                as="button"
                disabled={!(formik.isValid && formik.dirty) || isDisabled || isSavingProfile}
                type="submit"
              >
                {isSavingProfile ? 'Saving' : showSave ? 'Saved' : 'Save'}
              </PrimaryButton>
            </div>
            <Divider />
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default Profile;
